import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './GuidePost.css';

const RetatrutideGuide = () => {
  // Structured data for article
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Retatrutide Complete Guide 2025: Dosing, Weight Loss Results & Clinical Trials",
    "description": "Comprehensive guide to Retatrutide (LY3437943) - the triple agonist showing 24% weight loss. Learn about dosing, side effects, clinical trials, and how it compares to Tirzepatide and Semaglutide.",
    "image": "https://peptidedose.com/images/retatrutide-guide.jpg",
    "author": {
      "@type": "Organization",
      "name": "PeptideDose"
    },
    "publisher": {
      "@type": "Organization",
      "name": "PeptideDose",
      "logo": {
        "@type": "ImageObject",
        "url": "https://peptidedose.com/logo.png"
      }
    },
    "datePublished": "2025-01-15",
    "dateModified": "2025-01-15",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://peptidedose.com/guides/retatrutide-complete-guide"
    }
  };

  return (
    <article className="guide-post">
      <Helmet>
        <title>Retatrutide Complete Guide 2025: Dosing, Results & Clinical Trials | PeptideDose</title>
        <meta name="description" content="Everything you need to know about Retatrutide (LY3437943) - the triple agonist showing 24% weight loss. Dosing protocols, side effects, clinical trials, and comparisons." />
        <meta name="keywords" content="retatrutide guide, retatrutide dosing, retatrutide weight loss, LY3437943, retatrutide clinical trials, retatrutide vs tirzepatide" />
        
        <meta property="og:title" content="Retatrutide Complete Guide 2025" />
        <meta property="og:description" content="Comprehensive guide to Retatrutide - the triple agonist showing 24% weight loss in trials" />
        <meta property="og:type" content="article" />
        
        <script type="application/ld+json">
          {JSON.stringify(articleStructuredData)}
        </script>
      </Helmet>

      <div className="guide-hero">
        <div className="hero-content">
          <Link to="/guides" className="back-link">← Back to Guides</Link>
          <h1 className="guide-title">Retatrutide Complete Guide 2025</h1>
          <p className="guide-subtitle">Everything You Need to Know About the Triple Agonist Revolution</p>
          <div className="guide-meta">
            <span className="publish-date">Published: January 15, 2025</span>
            <span className="read-time">15 min read</span>
          </div>
        </div>
      </div>

      <div className="guide-content">
        <nav className="table-of-contents">
          <h2>Table of Contents</h2>
          <ul>
            <li><a href="#what-is-retatrutide">What is Retatrutide?</a></li>
            <li><a href="#how-it-works">How Retatrutide Works</a></li>
            <li><a href="#clinical-trials">Clinical Trial Results</a></li>
            <li><a href="#dosing-protocol">Dosing Protocol</a></li>
            <li><a href="#side-effects">Side Effects & Safety</a></li>
            <li><a href="#comparisons">Retatrutide vs Other GLP-1s</a></li>
            <li><a href="#calculator">How to Calculate Doses</a></li>
            <li><a href="#timeline">FDA Approval Timeline</a></li>
            <li><a href="#conclusion">Conclusion</a></li>
          </ul>
        </nav>

        <section id="what-is-retatrutide">
          <h2>What is Retatrutide?</h2>
          <p>
            Retatrutide (development code LY3437943) is an investigational triple agonist peptide developed by Eli Lilly that represents 
            the next evolution in metabolic medicine. Unlike single or dual agonist medications, Retatrutide simultaneously activates three 
            key receptors: GLP-1, GIP, and glucagon receptors.
          </p>
          <p>
            This triple mechanism of action has produced unprecedented results in clinical trials, with participants achieving average 
            weight loss of 24.2% over 48 weeks - surpassing all currently available medications including Tirzepatide (Mounjaro) and 
            Semaglutide (Wegovy).
          </p>

          <div className="key-facts">
            <h3>Retatrutide Key Facts:</h3>
            <ul>
              <li><strong>Type:</strong> Triple receptor agonist (GLP-1/GIP/Glucagon)</li>
              <li><strong>Developer:</strong> Eli Lilly and Company</li>
              <li><strong>Administration:</strong> Once-weekly subcutaneous injection</li>
              <li><strong>Phase:</strong> Currently in Phase 3 trials (TRIUMPH program)</li>
              <li><strong>Weight Loss:</strong> Up to 24.2% in 48 weeks</li>
              <li><strong>Expected Approval:</strong> 2026-2027</li>
            </ul>
          </div>
        </section>

        <section id="how-it-works">
          <h2>How Retatrutide Works: The Triple Agonist Advantage</h2>
          <p>
            Retatrutide's revolutionary approach targets three distinct metabolic pathways simultaneously, creating a synergistic effect 
            that maximizes weight loss and metabolic improvements:
          </p>

          <h3>1. GLP-1 Receptor Activation</h3>
          <p>
            Like Semaglutide, Retatrutide activates GLP-1 receptors, which:
          </p>
          <ul>
            <li>Slows gastric emptying, increasing satiety</li>
            <li>Reduces appetite through central nervous system effects</li>
            <li>Improves insulin sensitivity and glucose control</li>
            <li>Decreases glucagon secretion</li>
          </ul>

          <h3>2. GIP Receptor Activation</h3>
          <p>
            Similar to Tirzepatide, the GIP component adds:
          </p>
          <ul>
            <li>Enhanced insulin secretion in response to meals</li>
            <li>Improved lipid metabolism</li>
            <li>Potential neuroprotective effects</li>
            <li>Complementary appetite suppression</li>
          </ul>

          <h3>3. Glucagon Receptor Activation</h3>
          <p>
            The unique glucagon component provides:
          </p>
          <ul>
            <li>Increased energy expenditure and thermogenesis</li>
            <li>Enhanced fat oxidation</li>
            <li>Improved liver fat metabolism</li>
            <li>Prevention of metabolic adaptation during weight loss</li>
          </ul>

          <div className="mechanism-highlight">
            <p>
              <strong>The glucagon receptor activation is what sets Retatrutide apart</strong> from dual agonists like Tirzepatide. 
              This third mechanism increases calorie burning, helping to overcome the metabolic slowdown that typically occurs with 
              weight loss.
            </p>
          </div>
        </section>

        <section id="clinical-trials">
          <h2>Clinical Trial Results: Breaking Records</h2>
          
          <h3>Phase 2 Trial Results (Published in NEJM 2023)</h3>
          <p>
            The Phase 2 trial of Retatrutide involved 338 adults with obesity (BMI ≥30 or ≥27 with weight-related conditions) 
            and produced remarkable results:
          </p>

          <div className="trial-results">
            <h4>Weight Loss by Dose at 48 Weeks:</h4>
            <ul>
              <li><strong>1 mg:</strong> -8.7% body weight</li>
              <li><strong>4 mg:</strong> -12.9% body weight</li>
              <li><strong>8 mg:</strong> -17.3% body weight</li>
              <li><strong>12 mg:</strong> -24.2% body weight</li>
              <li><strong>Placebo:</strong> -2.1% body weight</li>
            </ul>
          </div>

          <h4>Other Metabolic Improvements:</h4>
          <ul>
            <li>HbA1c reduction: up to -2.02% in diabetes patients</li>
            <li>Systolic blood pressure: -10.1 mmHg average reduction</li>
            <li>Triglycerides: -42.3% reduction</li>
            <li>HDL cholesterol: +10.1% increase</li>
            <li>Liver fat: -82.4% reduction (measured by MRI-PDFF)</li>
          </ul>

          <h3>TRIUMPH Phase 3 Trial Program</h3>
          <p>
            Retatrutide is currently being evaluated in four Phase 3 trials under the TRIUMPH program:
          </p>
          <ul>
            <li><strong>TRIUMPH-1:</strong> Obesity without diabetes (n=2,500)</li>
            <li><strong>TRIUMPH-2:</strong> Obesity with type 2 diabetes (n=1,500)</li>
            <li><strong>TRIUMPH-3:</strong> Obesity with knee osteoarthritis (n=700)</li>
            <li><strong>TRIUMPH-4:</strong> Obesity with obstructive sleep apnea (n=400)</li>
          </ul>
          <p>
            Results are expected in 2025-2026, with potential FDA approval following shortly after.
          </p>
        </section>

        <section id="dosing-protocol">
          <h2>Retatrutide Dosing Protocol</h2>
          <p>
            Retatrutide follows a gradual titration schedule to minimize side effects while maximizing efficacy:
          </p>

          <div className="dosing-schedule" style={{
            background: 'rgba(20, 25, 45, 0.6)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '16px',
            padding: '25px',
            marginBottom: '30px'
          }}>
            <h3 style={{marginTop: '0'}}>Standard Titration Schedule:</h3>
            <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '20px'}}>
              <thead>
                <tr style={{borderBottom: '2px solid rgba(102, 126, 234, 0.3)'}}>
                  <th style={{padding: '12px', textAlign: 'left'}}>Weeks</th>
                  <th style={{padding: '12px', textAlign: 'left'}}>Dose</th>
                  <th style={{padding: '12px', textAlign: 'left'}}>Frequency</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{borderBottom: '1px solid rgba(102, 126, 234, 0.2)'}}>
                  <td style={{padding: '12px'}}>1-4</td>
                  <td style={{padding: '12px'}}>1 mg</td>
                  <td style={{padding: '12px'}}>Once weekly</td>
                </tr>
                <tr style={{borderBottom: '1px solid rgba(102, 126, 234, 0.2)'}}>
                  <td style={{padding: '12px'}}>5-8</td>
                  <td style={{padding: '12px'}}>2 mg</td>
                  <td style={{padding: '12px'}}>Once weekly</td>
                </tr>
                <tr style={{borderBottom: '1px solid rgba(102, 126, 234, 0.2)'}}>
                  <td style={{padding: '12px'}}>9-12</td>
                  <td style={{padding: '12px'}}>4 mg</td>
                  <td style={{padding: '12px'}}>Once weekly</td>
                </tr>
                <tr style={{borderBottom: '1px solid rgba(102, 126, 234, 0.2)'}}>
                  <td style={{padding: '12px'}}>13-16</td>
                  <td style={{padding: '12px'}}>8 mg</td>
                  <td style={{padding: '12px'}}>Once weekly</td>
                </tr>
                <tr style={{borderBottom: '1px solid rgba(102, 126, 234, 0.2)'}}>
                  <td style={{padding: '12px'}}>17+</td>
                  <td style={{padding: '12px'}}>12 mg</td>
                  <td style={{padding: '12px'}}>Once weekly (maximum)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Important Dosing Considerations:</h3>
          <ul>
            <li>Always start at 1 mg to assess tolerance</li>
            <li>Increase dose only if previous dose is well-tolerated</li>
            <li>Some patients achieve excellent results at 4-8 mg</li>
            <li>Not everyone needs to reach the 12 mg maximum dose</li>
            <li>Inject subcutaneously in abdomen, thigh, or upper arm</li>
            <li>Rotate injection sites to prevent lipodystrophy</li>
          </ul>

          <div className="calculator-cta">
            <h3>Calculate Your Retatrutide Dose</h3>
            <p>Use our precision calculator to determine exactly how much to draw in your syringe:</p>
            <Link to="/retatrutide-calculator" className="cta-button">
              Open Retatrutide Calculator →
            </Link>
          </div>
        </section>

        <section id="side-effects">
          <h2>Side Effects and Safety Profile</h2>
          <p>
            Like all GLP-1 agonists, Retatrutide can cause side effects, particularly during dose escalation. Most side effects are 
            gastrointestinal and tend to improve over time.
          </p>

          <h3>Common Side Effects (>10% of patients):</h3>
          <ul>
            <li><strong>Nausea:</strong> 35-45% (mostly mild to moderate)</li>
            <li><strong>Diarrhea:</strong> 20-25%</li>
            <li><strong>Vomiting:</strong> 15-20%</li>
            <li><strong>Constipation:</strong> 10-15%</li>
            <li><strong>Decreased appetite:</strong> Expected effect</li>
            <li><strong>Injection site reactions:</strong> 5-10%</li>
          </ul>

          <h3>Less Common but Serious Side Effects:</h3>
          <ul>
            <li><strong>Gallbladder issues:</strong> 2.6% (vs 0.6% placebo)</li>
            <li><strong>Increased heart rate:</strong> +4-6 bpm average</li>
            <li><strong>Pancreatitis:</strong> Rare but possible</li>
            <li><strong>Hypoglycemia:</strong> Mainly in diabetes patients on other medications</li>
          </ul>

          <h3>Safety Monitoring:</h3>
          <p>
            Patients on Retatrutide should be monitored for:
          </p>
          <ul>
            <li>Heart rate changes (especially first 12 weeks)</li>
            <li>Gallbladder symptoms</li>
            <li>Signs of pancreatitis</li>
            <li>Blood glucose in diabetes patients</li>
            <li>Injection site reactions</li>
          </ul>

          <div className="safety-note">
            <p>
              <strong>Important:</strong> The increase in heart rate with Retatrutide (related to glucagon activation) typically 
              stabilizes after 12 weeks and has not been associated with adverse cardiovascular outcomes in trials.
            </p>
          </div>
        </section>

        <section id="comparisons">
          <h2>Retatrutide vs Other GLP-1 Medications</h2>
          
          <div className="comparison-table" style={{
            background: 'rgba(20, 25, 45, 0.6)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(102, 126, 234, 0.2)',
            borderRadius: '16px',
            padding: '25px',
            marginBottom: '30px'
          }}>
            <h3 style={{marginTop: '0'}}>Head-to-Head Comparison</h3>
            <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '20px'}}>
              <thead>
                <tr style={{borderBottom: '2px solid rgba(102, 126, 234, 0.3)'}}>
                  <th style={{padding: '12px', textAlign: 'left'}}>Medication</th>
                  <th style={{padding: '12px', textAlign: 'left'}}>Mechanism</th>
                  <th style={{padding: '12px', textAlign: 'left'}}>Weight Loss</th>
                  <th style={{padding: '12px', textAlign: 'left'}}>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{borderBottom: '1px solid rgba(102, 126, 234, 0.2)'}}>
                  <td style={{padding: '12px'}}><strong>Retatrutide</strong></td>
                  <td style={{padding: '12px'}}>Triple (GLP-1/GIP/Glucagon)</td>
                  <td style={{padding: '12px'}}>24.2%</td>
                  <td style={{padding: '12px'}}>Phase 3</td>
                </tr>
                <tr style={{borderBottom: '1px solid rgba(102, 126, 234, 0.2)'}}>
                  <td style={{padding: '12px'}}>Tirzepatide</td>
                  <td style={{padding: '12px'}}>Dual (GLP-1/GIP)</td>
                  <td style={{padding: '12px'}}>22.5%</td>
                  <td style={{padding: '12px'}}>FDA Approved</td>
                </tr>
                <tr style={{borderBottom: '1px solid rgba(102, 126, 234, 0.2)'}}>
                  <td style={{padding: '12px'}}>Semaglutide</td>
                  <td style={{padding: '12px'}}>Single (GLP-1)</td>
                  <td style={{padding: '12px'}}>15-17%</td>
                  <td style={{padding: '12px'}}>FDA Approved</td>
                </tr>
                <tr style={{borderBottom: '1px solid rgba(102, 126, 234, 0.2)'}}>
                  <td style={{padding: '12px'}}>Liraglutide</td>
                  <td style={{padding: '12px'}}>Single (GLP-1)</td>
                  <td style={{padding: '12px'}}>5-8%</td>
                  <td style={{padding: '12px'}}>FDA Approved</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Why Retatrutide Shows Superior Results</h3>
          <ol>
            <li>
              <strong>Triple mechanism:</strong> Addresses appetite, metabolism, and energy expenditure simultaneously
            </li>
            <li>
              <strong>Glucagon advantage:</strong> Increases calorie burning, preventing metabolic adaptation
            </li>
            <li>
              <strong>Synergistic effects:</strong> Three pathways work together for enhanced results
            </li>
            <li>
              <strong>Comprehensive metabolic reset:</strong> Improves multiple health markers beyond weight
            </li>
          </ol>

          <h3>Choosing Between Options</h3>
          <p>
            While Retatrutide shows the most impressive results, consider:
          </p>
          <ul>
            <li><strong>Availability:</strong> Tirzepatide and Semaglutide are available now</li>
            <li><strong>Insurance coverage:</strong> FDA-approved options more likely covered</li>
            <li><strong>Side effect profile:</strong> Some may tolerate dual/single agonists better</li>
            <li><strong>Individual response:</strong> Results vary between individuals</li>
          </ul>
        </section>

        <section id="calculator">
          <h2>How to Calculate Retatrutide Doses</h2>
          <p>
            Accurate dosing is crucial for both safety and efficacy. Here's how to calculate your Retatrutide dose:
          </p>

          <h3>Step 1: Reconstitution</h3>
          <p>
            Retatrutide typically comes as a lyophilized powder that needs reconstitution:
          </p>
          <ul>
            <li>Common vial size: 10mg</li>
            <li>Add 1-2mL bacteriostatic water</li>
            <li>1mL = 10mg/mL concentration (easier math)</li>
            <li>2mL = 5mg/mL concentration (more dilute)</li>
          </ul>

          <h3>Step 2: Calculate Volume</h3>
          <p>
            Formula: Volume (mL) = Desired Dose (mg) ÷ Concentration (mg/mL)
          </p>
          
          <div className="calculation-examples">
            <h4>Examples with 10mg vial + 1mL water:</h4>
            <ul>
              <li>1mg dose = 0.1mL = 10 units on insulin syringe</li>
              <li>4mg dose = 0.4mL = 40 units</li>
              <li>8mg dose = 0.8mL = 80 units</li>
              <li>12mg dose = 1.2mL = 120 units (need larger syringe)</li>
            </ul>
          </div>

          <h3>Step 3: Draw and Inject</h3>
          <ol>
            <li>Clean vial top with alcohol</li>
            <li>Draw calculated amount into syringe</li>
            <li>Remove air bubbles</li>
            <li>Clean injection site</li>
            <li>Inject subcutaneously at 90° angle</li>
            <li>Rotate sites with each injection</li>
          </ol>

          <div className="calculator-cta">
            <h3>Make It Easy - Use Our Calculator</h3>
            <p>
              Skip the math and get precise dosing instantly with our visual syringe calculator:
            </p>
            <Link to="/retatrutide-calculator" className="cta-button primary">
              Retatrutide Dose Calculator →
            </Link>
          </div>
        </section>

        <section id="timeline">
          <h2>FDA Approval Timeline and Availability</h2>
          
          <h3>Current Timeline:</h3>
          <ul>
            <li><strong>2023:</strong> Phase 2 results published in NEJM</li>
            <li><strong>2024:</strong> TRIUMPH Phase 3 trials ongoing</li>
            <li><strong>2025:</strong> Expected Phase 3 results</li>
            <li><strong>2026:</strong> Potential FDA submission</li>
            <li><strong>2026-2027:</strong> Expected FDA approval</li>
          </ul>

          <h3>What This Means for Patients:</h3>
          <p>
            While Retatrutide shows incredible promise, it's still 1-2 years from market availability. In the meantime:
          </p>
          <ul>
            <li>Tirzepatide (Mounjaro/Zepbound) offers similar dual-agonist benefits</li>
            <li>Semaglutide (Wegovy/Ozempic) remains highly effective</li>
            <li>Clinical trial enrollment may be an option for some</li>
            <li>Compounded versions should be approached with caution</li>
          </ul>

          <h3>Future Developments:</h3>
          <p>
            Retatrutide is part of a new wave of multi-agonist medications:
          </p>
          <ul>
            <li><strong>CagriSema:</strong> Semaglutide + Cagrilintide combination</li>
            <li><strong>MariTide:</strong> Monthly GLP-1/GIP with extended action</li>
            <li><strong>Pemvidutide:</strong> Dual GLP-1/glucagon agonist</li>
          </ul>
        </section>

        <section id="conclusion">
          <h2>Conclusion: The Future of Weight Loss Medicine</h2>
          <p>
            Retatrutide represents a paradigm shift in obesity treatment. Its triple-agonist mechanism addresses the complex, 
            multifaceted nature of obesity by simultaneously targeting appetite, metabolism, and energy expenditure. The 24% 
            average weight loss seen in trials approaches what was previously only achievable through bariatric surgery.
          </p>

          <h3>Key Takeaways:</h3>
          <ul>
            <li>Retatrutide is the most effective weight loss medication in development</li>
            <li>Triple mechanism provides advantages over existing options</li>
            <li>Gradual dose titration is essential for tolerability</li>
            <li>FDA approval expected in 2026-2027</li>
            <li>Current alternatives like Tirzepatide offer similar benefits now</li>
          </ul>

          <p>
            As we await Retatrutide's approval, it's clear that we're entering a new era of metabolic medicine where significant, 
            sustainable weight loss is becoming increasingly achievable through pharmaceutical intervention.
          </p>
        </section>

        <div className="blog-cta-section">
          <h2>Ready to Start Your Journey?</h2>
          <p>Calculate precise doses for Retatrutide and other peptides with our professional calculator.</p>
          <div className="cta-buttons">
            <Link to="/retatrutide-calculator" className="cta-button primary">
              Retatrutide Calculator
            </Link>
            <Link to="/" className="cta-button secondary">
              Universal Peptide Calculator
            </Link>
          </div>
        </div>

        <div className="related-articles">
          <h3>Related Guides</h3>
          <ul>
            <li><Link to="/guides/tirzepatide-vs-semaglutide">Tirzepatide vs Semaglutide: Complete Comparison</Link></li>
            <li><Link to="/guides/glp1-side-effects">Managing GLP-1 Side Effects: A Practical Guide</Link></li>
            <li><Link to="/guides/peptide-reconstitution">Peptide Reconstitution: Step-by-Step Guide</Link></li>
          </ul>
        </div>
      </div>
    </article>
  );
};

export default RetatrutideGuide;